<template>
  <div
    class="flex flex-col md:flex-row justify-between items-center gap-4 mt-6"
  >
    <!-- Info -->
    <div class="text-sm text-gray-500">
      {{ $t("showing") }}
      <span class="font-semibold text-gray-700">{{
        pagination.count || 0
      }}</span>
      {{ $t("of") }}
      <span class="font-semibold text-gray-700">{{
        pagination.total || 0
      }}</span>
      {{ $t("results") }}
    </div>

    <!-- Controls -->
    <div
      class="flex items-center gap-1 bg-white shadow-sm rounded-lg p-1 border flex-wrap justify-center"
    >
      <!-- First -->
      <button
        :disabled="!pagination.links?.first || currentPage === 1"
        @click="changePage(1)"
        class="nav-btn"
        :title="$t('first_page')"
      >
        <i class="fas fa-angle-double-left"></i>
      </button>

      <!-- Prev -->
      <button
        :disabled="!pagination.links?.prev || currentPage === 1"
        @click="changePage(currentPage - 1)"
        class="nav-btn"
        :title="$t('previous_page')"
      >
        <i class="fas fa-angle-left"></i>
      </button>

      <!-- Page Numbers -->
      <template v-for="page in visiblePages" :key="page">
        <span
          v-if="page === '...'"
          class="px-2 py-1 text-gray-400 text-sm select-none"
        >
          &hellip;
        </span>

        <button
          v-else
          @click="changePage(page)"
          :class="[
            'page-btn',
            page === currentPage
              ? 'bg-blue-600 text-white shadow-sm'
              : 'text-gray-600 hover:bg-blue-50 hover:text-blue-600',
          ]"
        >
          {{ page }}
        </button>
      </template>

      <!-- Next -->
      <button
        :disabled="!pagination.links?.next || currentPage === totalPages"
        @click="changePage(currentPage + 1)"
        class="nav-btn"
        :title="$t('next_page')"
      >
        <i class="fas fa-angle-right"></i>
      </button>

      <!-- Last -->
      <button
        :disabled="!pagination.links?.last || currentPage === totalPages"
        @click="changePage(totalPages)"
        class="nav-btn"
        :title="$t('last_page')"
      >
        <i class="fas fa-angle-double-right"></i>
      </button>

      <!-- Divider -->
      <div class="w-px h-6 bg-gray-200 mx-1"></div>

      <!-- Jump -->
      <div class="flex items-center gap-1 px-1">
        <span class="text-xs text-gray-400 whitespace-nowrap">{{
          $t("go_to")
        }}</span>

        <input
          v-model.number="jumpInput"
          type="number"
          :min="1"
          :max="totalPages"
          @keydown.enter="doJump"
          @blur="doJump"
          class="jump-input"
          :placeholder="currentPage"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from "vue";
import { usePaginationStore } from "@/stores/pagination";

const pageStore = usePaginationStore();

const props = defineProps({
  pagination: {
    type: Object,
    required: true,
  },

  /**
   * مثال:
   * store-key="vehicles"
   * store-key="drivers"
   * store-key="users"
   */
  storeKey: {
    type: String,
    default: null,
  },
});

const emit = defineEmits(["change-page"]);

const currentPage = computed(() => props.pagination.current_page || 1);
const totalPages = computed(() => props.pagination.total_pages || 1);

const visiblePages = computed(() => {
  const total = totalPages.value;
  const current = currentPage.value;
  const delta = 2;

  const pages = [];

  if (total <= 7) {
    for (let i = 1; i <= total; i++) {
      pages.push(i);
    }

    return pages;
  }

  pages.push(1);

  const rangeStart = Math.max(2, current - delta);
  const rangeEnd = Math.min(total - 1, current + delta);

  if (rangeStart > 2) {
    pages.push("...");
  }

  for (let i = rangeStart; i <= rangeEnd; i++) {
    pages.push(i);
  }

  if (rangeEnd < total - 1) {
    pages.push("...");
  }

  pages.push(total);

  return pages;
});

const jumpInput = ref("");

function doJump() {
  const val = parseInt(jumpInput.value);

  if (
    !isNaN(val) &&
    val >= 1 &&
    val <= totalPages.value &&
    val !== currentPage.value
  ) {
    changePage(val);
  }

  jumpInput.value = "";
}

function changePage(page) {
  const p = parseInt(page);

  if (isNaN(p) || p < 1 || p > totalPages.value || p === currentPage.value) {
    return;
  }

  if (props.storeKey) {
    pageStore.setPage(props.storeKey, p);
  }

  emit("change-page", p);
}
</script>

<style scoped>
.nav-btn {
  @apply px-3 py-2 rounded-md text-gray-600 transition-all duration-200;
}

.nav-btn:hover:not(:disabled) {
  @apply bg-blue-100 text-blue-600;
}

.nav-btn:disabled {
  opacity: 0.35;
  cursor: not-allowed;
}

.page-btn {
  min-width: 34px;
  height: 34px;

  @apply px-2 rounded-md text-sm font-medium transition-all duration-200;
}

.jump-input {
  width: 64px;

  @apply text-center text-sm border border-gray-300 rounded-md py-1 px-1
         focus:outline-none focus:ring-2 focus:ring-blue-400
         focus:border-transparent transition-all;

  -moz-appearance: textfield;
}

.jump-input::-webkit-outer-spin-button,
.jump-input::-webkit-inner-spin-button {
  -webkit-appearance: none;
}
</style>
